import { useEffect, useState, useCallback, useMemo } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, authHeaders } from "@/lib/api";
import AdminLayout from "@/components/admin/AdminLayout";
import { Panel, SectionLabel, StatCard, BarRow, MonthBars } from "@/components/admin/cc";
import { L, eur } from "@/lib/labels";
import { Gauge, MapPin, Layers, Activity } from "lucide-react";

const MONTHS = ["jan", "feb", "mrt", "apr", "mei", "jun", "jul", "aug", "sep", "okt", "nov", "dec"];
const CATS = [
  { key: "hot", label: "HOT", tone: "hot" },
  { key: "high", label: "HIGH", tone: "warn" },
  { key: "normal", label: "NORMAAL", tone: "accent" },
  { key: "low", label: "LAAG", tone: "accent" },
];

export default function Analytics() {
  const { logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);

  const load = useCallback(async () => {
    try {
      const [s, l] = await Promise.all([
        api.get("/admin/stats", { headers: authHeaders() }),
        api.get("/admin/leads", { headers: authHeaders() }),
      ]);
      setStats(s.data); setLeads(l.data);
    } catch (e) { if (e?.response?.status === 401) logout(); }
  }, [logout]);

  useEffect(() => { load(); }, [load]);

  const monthData = useMemo(() => {
    const now = new Date();
    const buckets = [];
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      buckets.push({ key: `${d.getFullYear()}-${d.getMonth()}`, label: MONTHS[d.getMonth()], value: 0 });
    }
    const idx = Object.fromEntries(buckets.map((b, i) => [b.key, i]));
    leads.forEach((l) => {
      const d = new Date(l.created_at);
      const k = `${d.getFullYear()}-${d.getMonth()}`;
      if (k in idx) buckets[idx[k]].value += 1;
    });
    return buckets;
  }, [leads]);

  const byCat = stats?.by_category || {};
  const maxCat = Math.max(1, ...CATS.map((c) => byCat[c.key] || 0));
  const gem = (stats?.per_gemeente || []);
  const maxGem = Math.max(1, ...gem.map((g) => g.count));
  const types = (stats?.per_type || []).map((t) => ({ ...t, label: L.projectType(t.name) }));
  const maxType = Math.max(1, ...types.map((t) => t.count));
  const avgScore = leads.length ? Math.round(leads.reduce((s, l) => s + (l.score || 0), 0) / leads.length) : 0;
  const conv = stats?.total ? Math.round(((stats.by_status?.offerte_verzonden || 0) / stats.total) * 100) : 0;

  return (
    <AdminLayout title="Analytics" subtitle="Inzicht in leads, regio's en projecttypes">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testid="an-total" label="Totaal aanvragen" value={stats?.total ?? "—"} icon={Activity} />
        <StatCard testid="an-avgscore" label="Gem. leadscore" value={`${avgScore}/100`} sub="kwaliteit instroom" subTone="accent" icon={Gauge} />
        <StatCard testid="an-conv" label="Offerte-ratio" value={`${conv}%`} sub="aanvraag → offerte" subTone="pos" />
        <StatCard testid="an-value" label="Pipelinewaarde" value={stats ? eur(stats.total_value) : "—"} subTone="pos" />
      </div>

      <div className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-5">
        <Panel className="lg:col-span-2 p-6">
          <SectionLabel>Aanvragen per maand</SectionLabel>
          <MonthBars data={monthData} />
        </Panel>
        <Panel className="p-6">
          <SectionLabel>Leads per categorie</SectionLabel>
          <div data-testid="an-cats">
            {CATS.map((c) => <BarRow key={c.key} label={c.label} value={byCat[c.key] || 0} max={maxCat} tone={c.tone} />)}
          </div>
        </Panel>
      </div>

      <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Panel className="p-6">
          <SectionLabel right={<MapPin className="w-4 h-4 text-cc-faint" />}>Top gemeenten</SectionLabel>
          {gem.length === 0 ? <p className="font-body text-sm text-cc-faint">Geen data.</p> : (
            <div data-testid="an-gem">{gem.map((g) => <BarRow key={g.name} label={g.name} value={g.count} max={maxGem} />)}</div>
          )}
        </Panel>
        <Panel className="p-6">
          <SectionLabel right={<Layers className="w-4 h-4 text-cc-faint" />}>Projecttypes</SectionLabel>
          {types.length === 0 ? <p className="font-body text-sm text-cc-faint">Geen data.</p> : (
            <div data-testid="an-types">{types.map((t) => <BarRow key={t.name} label={t.label} value={t.count} max={maxType} tone="pos" />)}</div>
          )}
        </Panel>
      </div>
    </AdminLayout>
  );
}
